import React, {useMemo} from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,

    /* Hooks */
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {Collapse} from '@chakra-ui/react'

import {
    getGDIHideStyles,
    resolveCmsTextStyles
} from '../../utils/gdi-styles'
import {ChevronDownIcon} from '../../../../icons'

/* Reset `<img>` defaults for inline icons embedded in the authored
 * `TextForWeb` (e.g. tier circles next to "Creator" / "Muse" / "Icon"). */
const HTML_RESET_SX = {
    '& img': {display: 'inline-block', verticalAlign: 'middle'}
}

/* CMS authors wrap every `TextForWeb` value in `<p>...</p>`. The renderer
 * mounts each value inside a Chakra `Text` (which is itself a `<p>`), so
 * leaving the wrapper in place produces invalid nested `<p>` markup. This
 * helper strips a single outer `<p>` tag (optionally with attributes) so
 * the inner content can live directly inside the `Text`. */
const unwrapOuterParagraph = (html) => {
    if (typeof html !== 'string') return html
    const match = html.match(/^\s*<p\b[^>]*>([\s\S]*?)<\/p>\s*$/i)
    return match ? match[1] : html
}

const expandedFrom = (contentArea) => contentArea?.[0]?.ContentLink?.Expanded || null
const textOf = (textBlock) => unwrapOuterParagraph((textBlock?.TextForWeb || '').trim())
const styleOf = (textBlock) => textBlock?.GDITextBlockStylesPerViewType

/**
 * Maps the raw `GDIRewardTierTableBlock` CMS payload into the flat shape
 * already consumed by the loyalty-program renderers:
 *
 *   {
 *     levels: [{name, description, titleStyle, descriptionStyle}],
 *     tabs:   [{name, titleStyle, contents: [{name, description, titleStyle,
 *               descriptionStyle, cards: [{text, titleStyle}]}]}]
 *   }
 *
 * Each `*Style` value is the CMS-authored `*StylesPerViewType` array; when
 * non-empty `resolveCmsTextStyles` layers it over the theme defaults so
 * authors can override only the bits they care about.
 */
const transformCms = ({Columns, Sections}) => ({
    levels: (Columns || []).map((col) => {
        const tierBlock = expandedFrom(col?.TierName)
        const subtitleBlock = expandedFrom(col?.TierSubtitle)
        return {
            name: textOf(tierBlock),
            description: textOf(subtitleBlock),
            titleStyle: styleOf(tierBlock),
            descriptionStyle: styleOf(subtitleBlock)
        }
    }),
    tabs: (Sections || []).map((section) => {
        const titleBlock = expandedFrom(section?.GDIRewardTierTableBlockSectionTitle)
        return {
            name: textOf(titleBlock),
            titleStyle: styleOf(titleBlock),
            contents: (section?.GDIRewardTierTableBlockRows || []).map((row) => {
                const rowTitleBlock = expandedFrom(row?.GDIRewardTierTableBlockRowTitle)
                const rowDescBlock = expandedFrom(row?.RowDescription)
                return {
                    name: textOf(rowTitleBlock),
                    description: textOf(rowDescBlock),
                    titleStyle: styleOf(rowTitleBlock),
                    descriptionStyle: styleOf(rowDescBlock),
                    cards: (row?.CellValues || []).map((cell) => ({
                        text: unwrapOuterParagraph((cell?.TextForWeb || '').trim()) || null,
                        titleStyle: cell?.GDIRewardTierTableBlockStylesPerViewType
                    }))
                }
            })
        }
    })
})

const LevelsRow = ({styles, levels}) => (
    <Box {...styles.lpLevelsWrapper}>
        {levels.map((level, levelIndex) => {
            const title = resolveCmsTextStyles(
                level.titleStyle,
                styles.lpLevelName,
                HTML_RESET_SX
            )
            const description = level.description
                ? resolveCmsTextStyles(
                      level.descriptionStyle,
                      styles.lpLevelDescription,
                      HTML_RESET_SX
                  )
                : null
            return (
                <Box {...styles.lpLevel} key={`level-${levelIndex}`}>
                    <Text
                        {...title.directStyles}
                        sx={title.sxStyles}
                        dangerouslySetInnerHTML={{__html: level.name}}
                    />
                    {description && (
                        <Text
                            {...description.directStyles}
                            sx={description.sxStyles}
                            dangerouslySetInnerHTML={{__html: level.description}}
                        />
                    )}
                </Box>
            )
        })}
    </Box>
)
LevelsRow.propTypes = {
    styles: PropTypes.object.isRequired,
    levels: PropTypes.array.isRequired
}

const CardCell = ({styles, card}) => {
    /* Only render what CMS authored. Empty cells stay empty — authors can
     * embed inline HTML (e.g. an `<img>` tier circle or `<svg>`) in
     * `TextForWeb` when a visual is required. */
    if (!card?.text) return null
    const {directStyles, sxStyles} = resolveCmsTextStyles(
        card.titleStyle,
        styles.lpCardText,
        HTML_RESET_SX
    )
    return (
        <Text
            {...directStyles}
            sx={sxStyles}
            dangerouslySetInnerHTML={{__html: card.text}}
        />
    )
}
CardCell.propTypes = {
    styles: PropTypes.object.isRequired,
    card: PropTypes.object.isRequired
}

const MobileView = ({styles, data}) => (
    <Box {...styles.lpContainer}>
        <Box {...styles.lpHeading}>
            <LevelsRow styles={styles} levels={data.levels} />
        </Box>

        <Accordion defaultIndex={[0]} allowMultiple>
            {data.tabs.map((tab, tabIndex) => {
                const tabTitle = resolveCmsTextStyles(
                    tab.titleStyle,
                    styles.lpAccTitle,
                    HTML_RESET_SX
                )
                return (
                    <AccordionItem
                        {...styles.lpAccItem}
                        sx={{
                            backgroundColor: 'var(--chakra-colors-white)',
                            ...styles.lpAccItemSx
                        }}
                        key={`reward-tier-mobile-tab-${tabIndex}`}
                    >
                        <AccordionButton
                            data-testid={`reward-tier-accordion-button-${tabIndex}`}
                            {...styles.lpAccBtn}
                        >
                            <Box
                                as="span"
                                {...tabTitle.directStyles}
                                sx={tabTitle.sxStyles}
                                dangerouslySetInnerHTML={{__html: tab.name}}
                            />
                            <AccordionIcon as={ChevronDownIcon} />
                        </AccordionButton>
                        <AccordionPanel {...styles.lpAccPanel}>
                            {tab.contents.map((content, contentIndex) => {
                                const contentTitle = resolveCmsTextStyles(
                                    content.titleStyle,
                                    styles.lpContentName,
                                    HTML_RESET_SX
                                )
                                const contentDescription = content.description
                                    ? resolveCmsTextStyles(
                                          content.descriptionStyle,
                                          styles.lpContentDescription,
                                          HTML_RESET_SX
                                      )
                                    : null
                                return (
                                    <Box key={`reward-tier-mobile-content-${contentIndex}`}>
                                        <Text
                                            {...contentTitle.directStyles}
                                            sx={contentTitle.sxStyles}
                                            dangerouslySetInnerHTML={{__html: content.name}}
                                        />
                                        {contentDescription && (
                                            <Text
                                                {...contentDescription.directStyles}
                                                sx={contentDescription.sxStyles}
                                                dangerouslySetInnerHTML={{
                                                    __html: content.description
                                                }}
                                            />
                                        )}
                                        {content.cards.length > 0 && (
                                            <Box {...styles.lpCards}>
                                                {content.cards.map((card, cardIndex) => (
                                                    <Box
                                                        {...styles.lpCard}
                                                        sx={{
                                                            backgroundColor:
                                                                'var(--chakra-colors-white)'
                                                        }}
                                                        key={`reward-tier-mobile-card-${cardIndex}`}
                                                    >
                                                        <CardCell
                                                            styles={styles}
                                                            card={card}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )
                            })}
                        </AccordionPanel>
                    </AccordionItem>
                )
            })}
        </Accordion>
    </Box>
)
MobileView.propTypes = {
    styles: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired
}

const DesktopView = ({styles, data}) => {
    const [openItems, setOpenItems] = React.useState({})
    const toggleItem = (tabIndex, contentIndex) => {
        const key = `${tabIndex}-${contentIndex}`
        setOpenItems((prev) => ({...prev, [key]: !prev[key]}))
    }

    const leadTabTitle = data.tabs.length > 0 && data.tabs[0]?.name
        ? resolveCmsTextStyles(data.tabs[0].titleStyle, styles.lpAccTitle, HTML_RESET_SX)
        : null

    return (
        <Box {...styles.lpContainer}>
            <Box>
                <Box {...styles.lpLevels}>
                    {leadTabTitle && (
                        <Text
                            {...leadTabTitle.directStyles}
                            sx={leadTabTitle.sxStyles}
                            dangerouslySetInnerHTML={{__html: data.tabs[0].name}}
                        />
                    )}
                    <LevelsRow styles={styles} levels={data.levels} />
                </Box>
                <Box {...styles.lpTabs}>
                    {data.tabs.map((tab, tabIndex) => {
                        const tabHeading = tabIndex > 0
                            ? resolveCmsTextStyles(
                                  tab.titleStyle,
                                  styles.lpAccTitle,
                                  HTML_RESET_SX
                              )
                            : null
                        return (
                            <Box {...styles.lpTab} key={`reward-tier-desktop-tab-${tabIndex}`}>
                                {tabHeading && (
                                    <Box {...styles.lpAccBtn}>
                                        <Box
                                            as="span"
                                            {...tabHeading.directStyles}
                                            sx={tabHeading.sxStyles}
                                            dangerouslySetInnerHTML={{__html: tab.name}}
                                        />
                                    </Box>
                                )}
                                <Box>
                                    {tab.contents.map((content, contentIndex) => {
                                        const itemKey = `${tabIndex}-${contentIndex}`
                                        const isOpen = openItems[itemKey] || false
                                        const contentTitle = resolveCmsTextStyles(
                                            content.titleStyle,
                                            styles.lpContentName,
                                            HTML_RESET_SX
                                        )
                                        const contentDescription = content.description
                                            ? resolveCmsTextStyles(
                                                  content.descriptionStyle,
                                                  styles.lpContentDescription,
                                                  HTML_RESET_SX
                                              )
                                            : null
                                        return (
                                            <Box
                                                {...styles.lpContent}
                                                key={`reward-tier-desktop-content-${contentIndex}`}
                                            >
                                                <Box
                                                    {...styles.lpNameAndDesc}
                                                    onClick={() =>
                                                        toggleItem(tabIndex, contentIndex)
                                                    }
                                                >
                                                    <Text
                                                        {...contentTitle.directStyles}
                                                        sx={contentTitle.sxStyles}
                                                    >
                                                        {content.description && (
                                                            <Box
                                                                as="span"
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    transform: isOpen
                                                                        ? 'rotate(180deg)'
                                                                        : 'rotate(0deg)',
                                                                    transition: 'transform 0.2s'
                                                                }}
                                                            >
                                                                <ChevronDownIcon />
                                                            </Box>
                                                        )}
                                                        <Box
                                                            as="span"
                                                            dangerouslySetInnerHTML={{
                                                                __html: content.name
                                                            }}
                                                        />
                                                    </Text>
                                                    {contentDescription && (
                                                        <Collapse in={isOpen} animateOpacity>
                                                            <Text
                                                                {...contentDescription.directStyles}
                                                                sx={contentDescription.sxStyles}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: content.description
                                                                }}
                                                            />
                                                        </Collapse>
                                                    )}
                                                </Box>
                                                {content.cards.length > 0 && (
                                                    <Box {...styles.lpCards}>
                                                        {content.cards.map((card, cardIndex) => (
                                                            <Box
                                                                {...styles.lpCard}
                                                                key={`reward-tier-desktop-card-${cardIndex}`}
                                                            >
                                                                <CardCell
                                                                    styles={styles}
                                                                    card={card}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        )
                                    })}
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}
DesktopView.propTypes = {
    styles: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired
}

/**
 * GDIRewardTierTableBlock
 *
 * Renders a loyalty / rewards tier comparison table sourced entirely from
 * the CMS. The block expects to land inside `MyRewardsContent` so the
 * `MyRewardsStylesProvider` (theme key: `MyRewards`) is in scope — the
 * same theme that drives the legacy `LoyaltyProgram` partials. CMS-authored
 * style arrays on each text block override the theme defaults per element.
 */
const GDIRewardTierTableBlock = ({
    HideBlockOn,
    Columns,
    Sections
}) => {
    const styles = useMultiStyleConfig('MyRewards', {})
    const isMobile = useBreakpointValue({base: true, lg: false})

    const data = useMemo(
        () => transformCms({Columns, Sections}),
        [Columns, Sections]
    )

    const hideStyles = getGDIHideStyles(HideBlockOn)
    if (hideStyles.doNotLoadComponent) return null

    if (!data.levels.length || !data.tabs.length) return null

    return (
        <Box data-block-type="GDIRewardTierTableBlock" sx={hideStyles.sx}>
            {isMobile ? (
                <MobileView styles={styles} data={data} />
            ) : (
                <DesktopView styles={styles} data={data} />
            )}
        </Box>
    )
}

GDIRewardTierTableBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    Columns: PropTypes.array,
    Sections: PropTypes.array,
    GDIRewardTierTableBlockAudienceTargeting: PropTypes.object
}

export default GDIRewardTierTableBlock
